/**
 * Share Import Handler
 * Processes content shared from iOS Share Extension
 *
 * Architecture: Share Extension → App Groups → Plugin → This module → Article creation
 *
 * Supported types:
 * - URL: createPendingArticle
 * - Text: createSnippet
 * - PDF: createPdfArticle
 * - EPUB: createEpubArticle
 * - Text files (.txt/.md): createTextFileArticle
 */

import { Capacitor } from '@capacitor/core';
import ShareImportPlugin from '../plugins/shareImport';
import { supabase } from './supabase';
import { logger } from './logger';
import { platform } from './native';
import { toast } from 'sonner';
import { syncState, whenReady, when } from '@legendapp/state';
import {
  articles$,
  createPendingArticle,
  createSnippet,
  createTextFileArticle,
  createPdfArticle,
  createEpubArticle
} from '../store/articles$';
import { auth$ } from '../store/auth$';

import { apiConfig } from './apiConfig';
const SUPABASE_URL = apiConfig.supabaseUrl;

/**
 * Check if share import is supported on this platform
 */
const isShareSupported = () => platform.isIOS || platform.isAndroid;

/**
 * Main entry point - checks for pending shares and processes them
 * Single caller: App.jsx useEffect (cold start + warm start via appStateChange).
 */
export const checkAndProcessShares = async () => {
  // Early return if not supported on this platform
  if (!isShareSupported()) {
    return;
  }

  // Auth guard - skip if user not logged in
  const user = auth$.user.get();
  if (!user) {
    logger.info('Share import skipped - not authenticated');
    return;
  }

  // Wait for articles$ sync to be fully ready before processing shares
  // This prevents race condition where articles are created before sync handlers are initialized
  const syncStatus$ = syncState(articles$);

  if (!syncStatus$.isPersistLoaded.get()) {
    await whenReady(() => syncStatus$.isPersistLoaded);
  }

  if (!syncStatus$.isSyncEnabled.get()) {
    await when(() => syncStatus$.isSyncEnabled);
  }

  try {
    // Read pending shares from App Group via native plugin
    const result = await ShareImportPlugin.checkPendingShares();
    const shares = result.shares || [];

    // No shares to process
    if (shares.length === 0) {
      return;
    }

    logger.info(`Processing ${shares.length} pending share(s)`, { shareCount: shares.length });

    // Process each share sequentially
    let successCount = 0;
    let failureCount = 0;

    for (const share of shares) {
      try {
        const created = await processSingleShare(share);
        if (created) successCount++;
      } catch (error) {
        logger.error('Failed to process share', { share, error });
        failureCount++;
        // Continue processing other shares even if one fails
      }
    }

    // Show user feedback
    if (successCount > 0) {
      toast.success(
        successCount === 1
          ? 'Article added from share'
          : `${successCount} articles added from shares`
      );
    }

    if (failureCount > 0) {
      toast.error(
        failureCount === 1
          ? 'Failed to import 1 item'
          : `Failed to import ${failureCount} items`
      );
    }

  } catch (error) {
    logger.error('Share import failed', error);
    toast.error('Failed to import shared content');
  }
};

/**
 * Process a single share based on its type
 * Routes to the appropriate article creation function
 */
const processSingleShare = async (share) => {
  logger.info('Processing share', { type: share.type, id: share.id });

  // Reconciliation: if server already processed this URL share, skip local processing
  if (share.type === 'url' && share.id) {
    const existing = articles$[share.id]?.get();
    if (existing && existing.id === share.id) {
      logger.info('Share already processed server-side, skipping', { id: share.id });
      await ShareImportPlugin.clearPendingShare({ id: share.id });
      return false;
    }

    // Local observable might not have the article yet (initial sync incomplete)
    // or article was deleted (fieldDeleted removes from observable).
    // Check DB directly to avoid creating a phantom "Fetching article..." card.
    const { data: dbArticle } = await supabase
      .from('reedle_articles')
      .select('id, deleted')
      .eq('id', share.id)
      .maybeSingle();

    if (dbArticle) {
      logger.info('Share processed server-side but not in local store, clearing', { id: share.id, deleted: dbArticle.deleted });
      await ShareImportPlugin.clearPendingShare({ id: share.id });
      // Show toast only if the article is still live (not deleted by user)
      return !dbArticle.deleted;
    }
  }

  switch (share.type) {
    case 'url':
      await handleUrlShare(share);
      break;

    case 'text':
      await handleTextShare(share);
      break;

    case 'textfile':
      await handleTextFileShare(share);
      break;

    case 'pdf':
      await handlePdfShare(share);
      break;

    case 'epub':
      await handleEpubShare(share);
      break;

    default:
      logger.warn('Unknown share type', { share });
      throw new Error(`Unknown share type: ${share.type}`);
  }

  // Clear processed share from App Group
  await ShareImportPlugin.clearPendingShare({ id: share.id });
  logger.info('Share processed and cleared', { id: share.id, type: share.type });
  return true;
};

/**
 * Handle URL share (from Safari, browsers, etc.)
 */
const handleUrlShare = async (share) => {
  if (!share.url) {
    throw new Error('URL share missing url field');
  }

  // Pass share.id as articleId for reconciliation with server-side processing
  await createPendingArticle(share.url, null, share.id);
  logger.info('Created pending article from URL', { url: share.url, id: share.id });
};

/**
 * Handle plain text share (text selection, notes, etc.)
 */
const handleTextShare = async (share) => {
  if (!share.text) {
    throw new Error('Text share missing text field');
  }

  await createSnippet({
    title: '', // Will be auto-generated from content
    content: share.text,
    listId: null // Inbox
  });

  logger.info('Created snippet from text', { length: share.text.length });
};

/**
 * Handle text file share (.txt, .md, .markdown)
 */
const handleTextFileShare = async (share) => {
  if (!share.fileName || !share.content) {
    throw new Error('Text file share missing fileName or content');
  }

  await createTextFileArticle({
    fileName: share.fileName,
    content: share.content,
    listId: null // Inbox
  });

  logger.info('Created text file article', { fileName: share.fileName });
};

/**
 * Handle PDF file share
 * Fetches file from App Group shared container and creates File object
 */
const handlePdfShare = async (share) => {
  if (!share.fileName || !share.filePath) {
    throw new Error('PDF share missing fileName or filePath');
  }

  // Read file directly from native as base64 — avoids Capacitor local server
  // URL issues with internal private storage (/data/user/0/...) on Android
  const { base64 } = await ShareImportPlugin.readSharedFile({
    filePath: share.filePath
  });

  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });

  logger.info('handlePdfShare read from native', { fileName: share.fileName, size: blob.size });

  // Create File object (identical to <input type="file">)
  const file = new File([blob], share.fileName, {
    type: 'application/pdf'
  });

  // Pass to existing upload function
  await createPdfArticle(file, null); // null = Inbox

  logger.info('Created PDF article', {
    fileName: share.fileName,
    size: blob.size
  });
};

/**
 * Handle EPUB file share
 * Fetches file from App Group shared container and creates File object
 */
const handleEpubShare = async (share) => {
  if (!share.fileName || !share.filePath) {
    throw new Error('EPUB share missing fileName or filePath');
  }

  // Read file directly from native as base64 — avoids Capacitor local server
  // URL issues with internal private storage (/data/user/0/...) on Android
  const { base64 } = await ShareImportPlugin.readSharedFile({
    filePath: share.filePath
  });

  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/epub+zip' });

  logger.info('handleEpubShare read from native', { fileName: share.fileName, size: blob.size });

  // Create File object (identical to <input type="file">)
  const file = new File([blob], share.fileName, {
    type: 'application/epub+zip'
  });

  // Pass to existing upload function
  await createEpubArticle(file, null); // null = Inbox

  logger.info('Created EPUB article', {
    fileName: share.fileName,
    size: blob.size
  });
};

/**
 * Provision a PAT for the share extension and store in native shared storage.
 * Called on login. Generates a token_type='share_extension' token that won't
 * conflict with manual MCP tokens (token_type='manual').
 */
export const provisionShareToken = async () => {
  if (!isShareSupported()) return;

  const session = auth$.session.get();
  if (!session?.access_token) return;

  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/reedle-mcp/api/token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      logger.warn('Failed to provision share token', { status: response.status });
      return;
    }

    const { token } = await response.json();
    await ShareImportPlugin.setAuthToken({ token });
    logger.info('Share extension token provisioned');
  } catch (error) {
    logger.error('Error provisioning share token', error);
    // Non-fatal: share extension falls back to local-only
  }
};

/**
 * Revoke PAT and clear from native storage.
 * Called on logout.
 */
export const revokeShareToken = async () => {
  if (!isShareSupported()) return;

  try {
    await ShareImportPlugin.clearAuthToken();
    logger.info('Share extension token cleared from native storage');
  } catch (error) {
    logger.error('Error clearing share token', error);
  }

  // Server-side revocation (best effort)
  const session = auth$.session.get();
  if (session?.access_token) {
    try {
      await fetch(
        `${SUPABASE_URL}/functions/v1/reedle-mcp/api/token`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      );
    } catch {
      // Best effort — token is already cleared locally
    }
  }
};

/**
 * Process a file delivered via open-in (iOS Documents/Inbox).
 * Called from deeplink.js when appUrlOpen fires with a file:// URL.
 *
 * @param {string} fileUrl - file:// URL delivered by the OS
 * @param {string} fileName - display name for the file
 * @param {string} mimeType - 'application/pdf' or 'application/epub+zip'
 */
export const processOpenInFile = async (fileUrl, fileName, mimeType) => {
  const user = auth$.user.get();
  if (!user) {
    logger.info('Open-in file skipped - not authenticated');
    return;
  }

  const syncStatus$ = syncState(articles$);
  if (!syncStatus$.isPersistLoaded.get()) {
    await whenReady(() => syncStatus$.isPersistLoaded);
  }
  if (!syncStatus$.isSyncEnabled.get()) {
    await when(() => syncStatus$.isSyncEnabled);
  }

  try {
    const webPath = Capacitor.convertFileSrc(fileUrl);
    const response = await fetch(webPath);
    if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);

    const blob = await response.blob();
    const file = new File([blob], fileName, { type: mimeType });

    if (mimeType === 'application/pdf') {
      await createPdfArticle(file, null);
    } else {
      await createEpubArticle(file, null);
    }

    toast.success('Article added');
    logger.info('Open-in file processed', { fileName, mimeType, size: blob.size });
  } catch (error) {
    logger.error('Failed to process open-in file', { fileName, error });
    toast.error('Failed to import file');
  }
};

/**
 * Process a URL delivered via reedle://add?url=... open-in scheme.
 * Called from deeplink.js.
 *
 * @param {string} url - The target URL to add as an article
 */
export const processOpenInUrl = async (url) => {
  const user = auth$.user.get();
  if (!user) {
    logger.info('Open-in URL skipped - not authenticated');
    return;
  }

  const syncStatus$ = syncState(articles$);
  if (!syncStatus$.isPersistLoaded.get()) {
    await whenReady(() => syncStatus$.isPersistLoaded);
  }
  if (!syncStatus$.isSyncEnabled.get()) {
    await when(() => syncStatus$.isSyncEnabled);
  }

  try {
    await createPendingArticle(url, null, null);
    toast.success('Article added');
    logger.info('Open-in URL processed', { url });
  } catch (error) {
    logger.error('Failed to process open-in URL', { url, error });
    toast.error('Failed to add article');
  }
};

/**
 * Setup auth change listener to process queued shares after login
 * Call this during app initialization
 */
export const initShareImportAuthListener = () => {
  if (!isShareSupported()) {
    return;
  }

  auth$.isAuthenticated.onChange((isAuthenticated) => {
    if (isAuthenticated) {
      // Provision share extension token on login (background, non-blocking)
      provisionShareToken();
    }
  });

  logger.info('Share import auth listener initialized');
};
